<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ResponsesFilter } from '@/components/analysis/ResponsesSlideover.vue'
import type { AnswerAnalysisDTO, AnswerAnalysisGroupDTO, Event } from '@/types'

import QuestionAnalysisCard from '@/components/analysis/QuestionAnalysisCard.vue'
import ResponsesSlideover from '@/components/analysis/ResponsesSlideover.vue'
import { getAnswerAnalysis } from '@/services/answerAnalysisService'
import { AnswerAnalysisGroupKey, RegistrationStatus } from '@/types'

const props = defineProps<{
  event: Event
}>()

defineEmits<{
  (e: 'reload-event'): void
}>()

definePage({
  meta: {
    requiresAuth: true,
  },
})

const { t, n, d, locale } = useI18n()

const eventName = computed(() =>
  locale.value === 'de' ? props.event.nameDE : (props.event.nameEN ?? props.event.nameDE),
)

const analysis = ref<AnswerAnalysisDTO | null>(null)
const loading = ref(false)
const error = ref(false)
const statuses = ref<RegistrationStatus[]>([])
const groupByProduct = ref(false)

const statusOptions = computed(() =>
  Object.values(RegistrationStatus).map((status) => ({
    value: status,
    label: t(`enums.registrationStatus.${status}`),
  })),
)

let requestId = 0
const load = async () => {
  const current = ++requestId
  loading.value = true
  error.value = false
  try {
    const result = await getAnswerAnalysis(props.event.id, {
      statuses: statuses.value,
      groupByProduct: groupByProduct.value,
      language: locale.value,
    })
    if (current === requestId) {
      analysis.value = result
    }
  } catch (err) {
    console.error('Failed to load answer analysis:', err)
    if (current === requestId) {
      error.value = true
    }
  } finally {
    if (current === requestId) {
      loading.value = false
    }
  }
}

onMounted(load)
watch([statuses, groupByProduct, locale], load, { deep: true })

const groupLabel = (group: AnswerAnalysisGroupDTO) => {
  if (group.key === AnswerAnalysisGroupKey.None) return t('features.answerAnalysis.groups.none')
  if (group.key === AnswerAnalysisGroupKey.Other) return t('features.answerAnalysis.groups.other')
  return group.label || group.key
}

const drillFilter = ref<ResponsesFilter | null>(null)
const drillOpen = ref(false)
const openDrill = (filter: ResponsesFilter) => {
  drillFilter.value = filter
  drillOpen.value = true
}

const printedAt = ref(new Date())
const print = () => {
  printedAt.value = new Date()
  window.print()
}

const responseRate = computed(() =>
  analysis.value && analysis.value.totalRegistrations > 0
    ? analysis.value.respondentCount / analysis.value.totalRegistrations
    : 0,
)
</script>

<template>
  <div class="analysis-print-root flex flex-col gap-4">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p class="analysis-print-only m-0 text-sm text-muted">{{ eventName }}</p>
        <h2 class="m-0 text-2xl font-semibold">{{ t('features.answerAnalysis.title') }}</h2>
        <p class="analysis-no-print mt-1 mb-0 text-muted">
          {{ t('features.answerAnalysis.subtitle') }}
        </p>
        <p class="analysis-print-only m-0 mt-1 text-sm text-muted">
          {{ t('features.answerAnalysis.export.printedAt', { date: d(printedAt, 'datetime') }) }}
        </p>
      </div>
      <UButton
        v-if="analysis?.questions.length"
        class="analysis-no-print"
        :label="t('features.answerAnalysis.export.print')"
        icon="i-lucide-printer"
        color="neutral"
        variant="outline"
        @click="print"
      />
    </div>

    <div class="analysis-no-print flex flex-wrap items-center gap-4">
      <AppSelect
        v-model="statuses"
        multiple
        :options="statusOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('features.answerAnalysis.filters.allStatuses')"
        class="min-w-64"
      />
      <USwitch
        v-model="groupByProduct"
        :label="t('features.answerAnalysis.filters.groupByProduct')"
      />
    </div>

    <UAlert
      v-if="error"
      color="error"
      variant="soft"
      icon="i-lucide-circle-alert"
      :title="t('features.answerAnalysis.loadError')"
    />

    <div v-else-if="loading && !analysis" class="flex justify-center py-12">
      <UIcon name="i-lucide-loader-circle" class="size-6 animate-spin text-muted" />
    </div>

    <template v-else-if="analysis">
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-3" :class="{ 'opacity-60': loading }">
        <UCard>
          <p class="m-0 text-sm text-muted">
            {{ t('features.answerAnalysis.summary.registrations') }}
          </p>
          <p class="m-0 text-2xl font-semibold tabular-nums">
            {{ n(analysis.totalRegistrations) }}
          </p>
        </UCard>
        <UCard>
          <p class="m-0 text-sm text-muted">
            {{ t('features.answerAnalysis.summary.respondents') }}
          </p>
          <p class="m-0 text-2xl font-semibold tabular-nums">
            {{ n(analysis.respondentCount) }}
            <span class="text-base font-normal text-muted">
              ({{ n(responseRate, { style: 'percent', maximumFractionDigits: 0 }) }})
            </span>
          </p>
        </UCard>
        <UCard>
          <p class="m-0 text-sm text-muted">{{ t('features.answerAnalysis.summary.questions') }}</p>
          <p class="m-0 text-2xl font-semibold tabular-nums">{{ n(analysis.questions.length) }}</p>
        </UCard>
      </div>

      <UAlert
        v-if="analysis.questions.length === 0"
        color="neutral"
        variant="soft"
        icon="i-lucide-info"
        :title="t('features.answerAnalysis.noQuestions')"
      />

      <div class="flex flex-col gap-4" :class="{ 'opacity-60': loading }">
        <QuestionAnalysisCard
          v-for="(question, index) in analysis.questions"
          :key="question.questionId"
          :question="question"
          :index="index"
          :respondent-count="analysis.respondentCount"
          :event-id="event.id"
          :ai-summary-available="analysis.aiSummaryAvailable"
          :groups="analysis.groups"
          :group-label="groupLabel"
          @drill="openDrill"
        />
      </div>
    </template>

    <ResponsesSlideover
      v-model:open="drillOpen"
      :event-id="event.id"
      :statuses="statuses"
      :filter="drillFilter"
    />
  </div>
</template>

<style>
.analysis-print-only {
  display: none;
}

@media print {
  body * {
    visibility: hidden;
  }

  .analysis-print-root,
  .analysis-print-root * {
    visibility: visible;
  }

  .analysis-print-root {
    position: absolute;
    inset: 0 auto auto 0;
    width: 100%;
  }

  .analysis-no-print {
    display: none !important;
  }

  .analysis-print-only {
    display: block;
  }

  .analysis-card {
    break-inside: avoid;
    box-shadow: none;
  }
}
</style>
